import { LineChart } from "@mui/x-charts";
import { useXScale, useDrawingArea } from "@mui/x-charts";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import dayjs from "dayjs";
import {
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";

/**
 * CommonLineGraph - A reusable dual-line chart component for statistics visualization.
 * Renders one or two area line series with month grouping labels on the x-axis.
 * Supports dynamic y-axis scaling, amount formatting, and CSS variable theming.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number[]} props.graphDataFirst - Primary data series (required)
 * @param {number[]} [props.graphDataSecond] - Secondary data series (optional)
 * @param {string[]} props.xAxisLabels - Date/day labels for x-axis (e.g., ["2025-01-01", ...] or ["Sun", "Mon"])
 * @param {string} props.graphDataFirstColor - CSS variable for first series color (e.g., "var(--settled)")
 * @param {string} [props.graphDataSecondColor="var(--text-primary)"] - CSS variable for second series color
 * @param {number[]} [props.defaultDataItemsForXaxis] - Custom x-axis indices (falls back to 0..n)
 * @param {string} [props.period] - Time period string
 * @param {boolean} [props.amountFormatRequiredForLegend=true] - Whether to format tooltip values as INR amounts
 * @param {string} [props.amountFormatForLegend] - Amount unit for legend/tooltip (e.g., "thousand", "lakh", "crore")
 * @returns {JSX.Element} Rendered LineChart with custom styling and month labels
 */
const CommonLineGraph = ({
  graphDataFirst,
  graphDataSecond,
  xAxisLabels, // e.g. ["2025-01-01", "2025-01-02", ..., "2025-02-01"] or ["Sun", "Mon"]
  graphDataFirstColor,
  graphDataSecondColor = "var(--text-primary)",
  defaultDataItemsForXaxis,
  period,
  amountFormatRequiredForLegend = true,
  amountFormatForLegend,
}) => {
  let xAxisArray = xAxisLabels;

  //NOTE: this is fallback function to check wheather data (xAxisLabels) coming from backend is day or date format
  const isDateArray = (data) =>
    data.every((item) => /^\d{4}-\d{2}-\d{2}$/.test(item));

  // Abbreviation map for amount units in y-axis labels
  const abbrMap = {
    Lakhs: "L",
    Crores: "Cr",
    Millions: "M",
    Billions: "B",
    Thousands: "K",
  };

  const formattedGraphDataFirst = graphDataFirst.map((value) => value);
  const formattedGraphDataSecond = graphDataSecond
    ? graphDataSecond.map((value) => value)
    : [];

  // --- Dynamic min/max ---
  const allData = [...formattedGraphDataFirst, ...formattedGraphDataSecond];
  const dataMax = Math.max(...allData);

  // Minimum always 0; maximum adds 20% headroom, fallback to 1 if all zeros
  const yAxisMin = 0;
  const yAxisMax = dataMax > 0 ? dataMax * 1.2 : 1;

  // Determine abbreviation suffix (K, L, M, Cr, B) based on max value
  const yAxisUnit = getAbbreviation(yAxisMax);

  // --- CSS Variable Resolution ---
  // Extracts computed value from CSS custom property (removes "var()" wrapper)
  const getCssVar = (variable) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();

  // Resolve color CSS variables to actual color values for MUI Charts
  const graphDataFirstColorForSeries = getCssVar(
    graphDataFirstColor.replace(/var\(|\)/g, ""),
  );
  const graphDataSecondColorForSeries = getCssVar(
    graphDataSecondColor.replace(/var\(|\)/g, ""),
  );

  // --- Month Grouping Logic ---
  // Groups consecutive dates by month for bottom-axis month labels
  const monthGroups = [];
  let currentMonth = null;

  xAxisLabels.forEach((date, index) => {
    const month = dayjs(date).format("MMM YYYY");
    if (!currentMonth || currentMonth.name !== month) {
      currentMonth = { name: month, start: index, end: index };
      monthGroups.push(currentMonth);
    } else {
      currentMonth.end = index;
    }
  });

  /**
   * MonthLabels - Custom SVG component rendering month range labels below x-axis.
   * Draws vertical ticks, horizontal lines, and abbreviated month names centered
   * over each month's date range. Uses MUI Charts' internal scale and drawing area.
   */
  const MonthLabels = ({ monthGroups }) => {
    const scale = useXScale();
    const { top, height } = useDrawingArea();

    if (
      !scale ||
      !monthGroups ||
      monthGroups.length === GlobalConfiguration?.lengthCheckZero
    )
      return null;

    // Y position for labels: below chart area + configured offset
    const yLine =
      top + height + SettingsStatisticsConfiguration?.graphRangeLabelOffsetY;
    const tickHeight =
      SettingsStatisticsConfiguration?.graphRangeLabelEdgeHeight;

    return (
      <g className="month-labels-group">
        {monthGroups.map((group, idx) => {
          const isSingleDate = group.start === group.end;

          let xStart, xEnd;
          if (isSingleDate) {
            // Single date: center label with half-width on each side
            const xCenter = scale(group.start);
            xStart =
              xCenter -
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
            xEnd =
              xCenter +
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
          } else {
            // Multi-date range: span from first to last date
            xStart = scale(group.start);
            xEnd = scale(group.end);
          }

          // Skip if scale returned invalid values
          if (
            xStart === undefined ||
            xEnd === undefined ||
            isNaN(xStart) ||
            isNaN(xEnd)
          ) {
            return null;
          }

          const xMid = (xStart + xEnd) / GlobalConfiguration?.lengthCheckTwo;
          // Abbreviate month name to first 3 chars (e.g., "Jan 2025" -> "Jan")
          const monthName = group.name
            ? group.name.slice(
                GlobalConfiguration?.lengthCheckZero,
                GlobalConfiguration?.lengthCheckThree,
              )
            : "";
          const gap = SettingsStatisticsConfiguration?.graphMonthLabelGapX;
          return (
            <g key={idx}>
              {/* Left vertical tick */}
              <line
                x1={xStart}
                x2={xStart}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />

              {/* Left horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xStart < xMid - gap && (
                <line
                  x1={xStart}
                  x2={xMid - gap}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Month label text */}
              <text
                x={xMid}
                y={yLine}
                fill="var(--text-secondary)"
                fontSize="var(--font-xs)"
                fontWeight="var(--font-normal)"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {monthName}
              </text>

              {/* Right horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xMid + gap < xEnd && (
                <line
                  x1={xMid + gap}
                  x2={xEnd}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Right vertical tick */}
              <line
                x1={xEnd}
                x2={xEnd}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />
            </g>
          );
        })}
      </g>
    );
  };

  /**
   * Converts a numeric value to abbreviated string based on given unit.
   * E.g., convertValue(150000, "L") -> "1.5L"
   */
  function convertValue(value, abbreviation) {
    if (value === GlobalConfiguration?.lengthCheckZero)
      return GlobalConfiguration?.dashboardPrimaryCount.slice(-1);

    const scales = {
      "K": 1e3,
      "L": 1e5,
      "Cr": 1e7,
      "M": 1e6,
      "B": 1e9,
      "": 1,
    };

    const divisor = scales[abbreviation] ?? 1;
    const result = value / divisor;

    // Trim trailing zeros, keep 2 decimal places max
    return `${parseFloat(result.toFixed(2))}${abbreviation}`;
  }

  /**
   * Determines the appropriate abbreviation suffix for a given max value.
   * Thresholds follow numbering system (K, Lakh, Crore, M, B).
   */
  function getAbbreviation(maxValue) {
    const thresholds = [
      { limit: 1e3, suffix: "K" }, // >= 1K
      { limit: 1e5, suffix: "L" }, // >= 100K (1 Lakh)
      { limit: 1e6, suffix: "M" }, // >= 10 Lakhs (1 Million)
      { limit: 1e7, suffix: "Cr" }, // >= 100 Lakhs (1 Crore)
      { limit: 1e9, suffix: "B" }, // >= 1000 Crores (1 Billion)
      { limit: 0, suffix: "" }, // default, no abbreviation
    ];

    const match = thresholds.find((t) => maxValue >= t.limit);
    return match.suffix;
  }

  // for week days logic wrtten
  // xAxisArray = xAxisLabels.map(
  //   (xAxisLabels) => xAxisLabels?.slice(0, 3), // just first 3 letters
  // );

  return (
    <div className="relative">
      <LineChart
        grid={{ vertical: true }}
        tooltip={{ trigger: "item" }}
        margin={{
          right: SettingsStatisticsConfiguration.graphPaddingRight, // right padding
          left: SettingsStatisticsConfiguration.graphPaddingLeft, // left padding
        }}
        series={
          graphDataSecond
            ? [
                // First series (primary)
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
                // Second series (secondary)
                {
                  id: "second",
                  data: formattedGraphDataSecond,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataSecondColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
            : [
                // Single series mode
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
        }
        className={`settings-statistics-linechart`}
        xAxis={[
          {
            scaleType: "linear",
            // Use custom indices if provided, otherwise 0..n
            tickInterval:
              defaultDataItemsForXaxis || xAxisArray.map((_, i) => i),
            valueFormatter: (value) => {
              const item = xAxisArray[value];
              if (!item) return "";
              const d = dayjs(item);
              // If valid date, show day number (DD); otherwise show first 3 chars
              return d.isValid()
                ? d.format("DD")
                : item.slice(
                    GlobalConfiguration?.lengthCheckZero,
                    GlobalConfiguration?.lengthCheckThree,
                  );
            },
            data: defaultDataItemsForXaxis,
            tickMinStep: GlobalConfiguration?.lengthCheckOne,
            // Add padding on left/right of chart for first/last points
            min: -(
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              SettingsStatisticsConfiguration?.graphXAxisPaddingPercent
            ),
            max:
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              (GlobalConfiguration?.lengthCheckOne +
                SettingsStatisticsConfiguration?.graphXAxisPaddingPercent),
            showGrid: true,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => {
              // If user selected a specific unit, format with that unit's abbreviation
              if (
                amountFormatForLegend &&
                value !== GlobalConfiguration?.lengthCheckZero
              ) {
                return `${formatTotalBalance(value, "amount")
                  .amount.replace(",", "")
                  .concat("" + abbrMap?.[amountFormatForLegend] || "")}`;
              }
              // Otherwise use auto-detected abbreviation based on data range
              return convertValue(value, yAxisUnit);
            },
            scaleType: "linear",
            tickNumber: SettingsStatisticsConfiguration?.graphYAxisTickCount,
            min: yAxisMin,
            max: yAxisMax,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        style={{
          height: "var(--statistic-graph-height)",
          width: "var(--width-full)",
        }}
        sx={{
          // ---------------First series styles----------------//
          "& .MuiLineElement-series-first": {
            stroke: graphDataFirstColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-first": {
            fill: graphDataFirstColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          /* Styling of marks for first*/
          "& .MuiMarkElement-series-first": {
            fill: "var(--text-primary)",
            stroke: graphDataFirstColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          "& .MuiMarkElement-series-first:hover": {
            fill: "var(--text-primary)",
            stroke: graphDataFirstColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },

          // -----------Second series styles----------------//
          "& .MuiLineElement-series-second": {
            stroke: graphDataSecondColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-second": {
            fill: graphDataSecondColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          /* Styling of marks for second*/
          "& .MuiMarkElement-series-second": {
            fill: "var(--text-primary)",
            stroke: graphDataSecondColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          "& .MuiMarkElement-series-second:hover": {
            fill: "var(--text-primary)",
            stroke: graphDataSecondColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          // ------------Common styling---------------------//

          "& .MuiChartsAxisHighlight-root": {
            strokeDasharray:
              SettingsStatisticsConfiguration.zeroStrokeWidthForLineGraph,
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            stroke: "var(--text-primary)",
          },
          // Axis lines
          "& .MuiChartsAxis-root line": {
            stroke: "var(--text-primary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
          },
          // Axis tick marks
          "& .MuiChartsAxis-tick text": {
            fill: "var(--text-primary)",
            fontSize: "var(--font-sm)",
          },
          "& .MuiChartsAxis-tickLabel": {
            fill: "var(--text-primary) !important",
          },

          // Hide horizontal grid lines (cleaner look)
          "& .MuiChartsGrid-horizontalLine": {
            display: "none",
          },

          // Hide Y-axis line and ticks (keep numeric labels only)
          "& .MuiChartsAxis-left .MuiChartsAxis-line": {
            display: "none",
          },
          "& .MuiChartsAxis-left .MuiChartsAxis-tick": {
            display: "none",
          },

          // Style vertical grid lines
          "& .MuiChartsGrid-verticalLine": {
            stroke: "var(--text-secondary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            opacity: SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
        }}
      >
        {/* Render month labels only when x-axis contains full dates */}
        {isDateArray(xAxisLabels) &&
          monthGroups?.length > GlobalConfiguration?.lengthCheckZero && (
            <MonthLabels monthGroups={monthGroups} />
          )}
      </LineChart>
    </div>
  );
};

export default CommonLineGraph;
